import { EventEmitter, Injectable, OnDestroy } from '@angular/core'
import { Router, RoutesRecognized } from '@angular/router'
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs'
import { first } from 'rxjs/operators'
import { MapMode, Vertical } from '../models/Vertical'
import { BaseFilter } from '../models/BaseFilter'
import { AuthenticationService } from './auth.service'
import { ComponentBuilderService } from './component-builder-service.service'

@Injectable()
export class ContextService implements OnDestroy {
  filters: Record<string, BaseFilter>

  poi = null
  poiLabel = null
  poiRegion = null
  boundingBox: any[]
  legendMaxValue: string
  isochroneConfig: any

  filterChanges = new EventEmitter<any>()
  poiChanges = new EventEmitter<any>()
  mapModeChanges = new EventEmitter<any>()
  mapLegendChanges = new EventEmitter<any>()
  mapSourceChanges = new EventEmitter<any>()
  isochroneConfigChanges = new EventEmitter<any>()
  currentKeyIndicator = new EventEmitter<any>()
  deliveriesChanges = new EventEmitter<any>()

  routerSubscription = new Subscription()

  mapMode = MapMode.NEIGHBORHOOD

  city: { id: string; name: string; bbox: number[]; region_id: string }

  isoline: any = null

  organization: {
    id: string
    name: string
    terrunit_region_id: string
    config: {
      parameters: any
      verticals: any[]
    }
  }
  keyIndicatorLabels = {
    nvisitors: 'Visitors',
    nvisits: 'Visits',
  }
  keyIndicatorOptions = []
  keyIndicator = { value: 'nvisitors', label: 'Visitors' }

  $minMaxFilter = new BehaviorSubject<number[]>([])
  $currentTags = new BehaviorSubject<number[][]>([])
  $currentVertical = new BehaviorSubject<Vertical>(new Vertical())
  $verticals = new BehaviorSubject<Vertical[]>([])
  $orgPromise: Promise<any> = null
  isPrinting = new BehaviorSubject<boolean>(false)

  filterBarOpen = true
  currentBasemap = null

  densityMode = new BehaviorSubject<boolean>(false)

  constructor(
    private router: Router,
    public auth: AuthenticationService,
    protected builder: ComponentBuilderService,
  ) {}

  init() {
    this.resetOrganization()
    this.resetContext()
    this.routerSubscription = this.router.events.subscribe((event: any) => {
      if (event instanceof RoutesRecognized) {
        if (this.auth.isLoggedIn()) {
        }
      }
    })
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe()
  }

  get vertical(): Vertical {
    return this.$currentVertical.getValue()
  }

  get verticals(): Vertical[] {
    return this.$verticals.getValue()
  }

  toggleIsPrinting() {
    this.isPrinting.next(!this.isPrinting.getValue())
  }

  setFilter(filter: BaseFilter) {
    this.filters[filter.id] = filter
    this.notifyFilterChanges()
  }

  setMapMode(value: any) {
    this.poi = null
    this.mapMode = value
    this.mapModeChanges.emit(this.filters)
  }

  setPoi(poi) {
    this.poi = (poi && poi.value) || poi
    this.poiLabel = poi && poi.label
    this.poiRegion = poi && poi.region_id
    this.poiChanges.emit(poi)
  }

  setBoundingBox(bbox) {
    this.boundingBox = bbox
    this.mapSourceChanges.emit({
      bbox: bbox,
    })
  }

  setLegendMaxValue(value) {
    this.legendMaxValue = value
    this.mapLegendChanges.emit({
      maxValue: this.legendMaxValue,
    })
  }

  setIsochroneConfig(config) {
    if (config.enabled !== undefined) {
      this.isochroneConfig.enabled = config.enabled
    }
    if (config.mode) {
      this.isochroneConfig.mode = config.mode
    }
    if (config.time) {
      this.isochroneConfig.time = config.time
    }
    this.isochroneConfigChanges.emit(this.isochroneConfig)
  }

  setCity(city: any) {
    const vertical = this.vertical
    vertical.delivery = vertical.deliveries[city.name][0]
    this.$currentVertical.next(vertical)
    this.city = vertical.delivery.territory
    this.deliveriesChanges.emit({
      city: this.city,
      delivery: this.vertical.delivery,
    })
  }

  setDelivery(delivery: any) {
    const vertical = this.vertical
    vertical.delivery = delivery
    this.$currentVertical.next(vertical)
    const useMobility = this.poi && delivery.filtersMobility.length
    const filters = useMobility
      ? vertical.delivery.filtersMobility
      : vertical.delivery.filters
    const target = vertical.delivery.target
    filters.forEach((f) => {
      this.filters[f.id].value = f.value
      if (f.subfilters.length) {
        f.subfilters.forEach((sf) => {
          const _sf = this.filters[f.id].subfilters.find(
            (_sfI) => _sfI.id === sf.id,
          )
          _sf.value = sf.value
        })
      }
    })
    if (this.filters['target']) {
      this.filters['target'].value = target.value
      if (target.subfilters.length) {
        target.subfilters.forEach((sf) => {
          const _sf = this.filters['target'].subfilters.find(
            (_sfI) => _sfI.id === sf.id,
          )
          if (_sf) {
            _sf.value = sf.value
          }
        })
      }
    }
    this.deliveriesChanges.emit({
      city: this.city,
      delivery: vertical.delivery,
    })
  }

  isEntirePopulationActive() {
    const target = this.vertical.delivery.target
    return target && target.subfilters.find((f) => !!f.value) === undefined
  }

  setKeyIndicator(value: string) {
    const indicator = this.keyIndicatorOptions.find((el) => el.value === value)
    this.keyIndicator = indicator
    this.currentKeyIndicator.next(value)
    this.vertical.delivery.variablesKey = value
    this.setDelivery(this.vertical.delivery)
  }

  parseCurrentVertical() {
    const url = window.location.hash || window.location.pathname
    const verticalUrl = url.split('/')[1]
    const vertical = this.verticals.find((item) => {
      return item.slug === verticalUrl
    })
    return vertical || new Vertical()
  }

  getContext() {
    const params = {
      poi: this.poi,
      poiRegion: this.poiRegion,
      bbox: this.boundingBox,
      mapMode: this.mapMode,
      legend: {
        maxValue: this.legendMaxValue,
      },
      isochroneConfig: this.isochroneConfig,
      delivery: this.vertical.delivery,
      city: this.city,
      tags: this.$currentTags.getValue(),
      minmax: this.$minMaxFilter.getValue(),
    }
    for (const name in this.filters) {
      if (this.filters[name].subfilters.length > 0) {
        params[name] = {}
        this.filters[name].subfilters.forEach((filter: BaseFilter) => {
          params[name][filter.key_name] = filter.value
        })
      } else {
        params[name] = this.filters[name].value
      }
    }
    return params
  }

  getFilters(): Record<string, BaseFilter> {
    return this.filters
  }

  getDeliveryFilters(vertical = null) {
    vertical = vertical || this.vertical
    return this.poi && vertical.delivery.filtersMobility.length > 0
      ? vertical.delivery.filtersMobility
      : vertical.delivery.filters
  }

  /**
   * Ensure non-mobility filters are returned (there are services in
   * the mobility sections that uses dst_abs data and needs access to
   * dst_abs filters)
   */
  getDeliveryFiltersDst(vertical = null) {
    vertical = vertical || this.vertical
    return vertical.delivery.filters
  }

  getLegend(): any {
    return {
      maxValue: this.legendMaxValue,
    }
  }

  resetContext(vertical: Vertical = null) {
    this.filters = {}
    this.mapMode = MapMode.NEIGHBORHOOD
    this.densityMode.next(false)
    // It defines data for components load before deliveries metadata are loaded
    this.city = {
      name: 'Madrid',
      id: '28079',
      bbox: [-3.745473, 40.398172, -3.662204, 40.460955],
      region_id: 'ES',
    }
    if (vertical) {
      this.mapMode = vertical.delivery.mapMode
      this.city = vertical.delivery.territory
      this.getDeliveryFilters(vertical).forEach((filter: BaseFilter) => {
        this.filters[filter.id] = filter
      })
    }

    this.legendMaxValue = 'max'
    this.isochroneConfig = { enabled: false, mode: null, time: null }
  }

  resetOrganization() {
    this.$verticals.next([])
    this.organization = {
      id: '',
      name: '',
      terrunit_region_id: '',
      config: {
        parameters: {},
        verticals: [],
      },
    }
  }

  getMapMode() {
    return this.mapMode
  }

  private notifyFilterChanges() {
    this.filterChanges.emit({
      filter: this.getContext(),
    })
  }

  isVisitsAvailable() {
    return this.keyIndicatorOptions.length >= 2
  }

  setupKeyIndicators() {
    const keys = Object.keys(this.vertical.delivery.config.variables)
    this.keyIndicatorOptions = keys
      // Force nvisitors first
      .sort((key) => (key === 'nvisitors' ? -1 : 1))
      .map((key) => ({ value: key, label: this.keyIndicatorLabels[key] }))
    if (!this.keyIndicator) {
      this.keyIndicator = { ...this.keyIndicatorOptions[0] }
    }
    this.deliveriesChanges.emit()
  }

  initOrganization() {
    if (this.$verticals.getValue().length > 0) {
      return Promise.resolve()
    }
    if (this.$orgPromise) {
      return firstValueFrom(this.$verticals.pipe(first()))
    }

    this.$verticals.next([])
    this.$currentVertical.next(new Vertical())

    const org_id = this.auth.getUser()?.user?.org_id || ''

    console.log('org_id', this.auth.getUser())

    return this.$orgPromise
  }

  private initDeliveries(org_id, vertical) {
    const isAdmin = this.auth.getUser()?.user?.is_admin || false
  }

  toggleFilterBar(val) {
    this.filterBarOpen = val
  }

  createComponentInstance(component: any, attachView = true) {
    return this.builder.createInstance(component, attachView)
  }

  hasAggregates() {
    const hasConfig =
      this.vertical.delivery &&
      this.vertical.delivery.sections &&
      this.vertical.delivery.sections.aggregates
    const config = hasConfig && this.vertical.delivery.sections.aggregates
    const hasAgg = config && config.destination
    return !!this.poi || hasAgg
  }

  canClearPoi() {
    const hasConfig =
      this.vertical.delivery &&
      this.vertical.delivery.sections &&
      this.vertical.delivery.sections.aggregates
    const config = hasConfig && this.vertical.delivery.sections.aggregates
    const hasAgg = config && config.destination
    return !this.poi || hasAgg
  }

  canShowReport() {
    return (
      this.vertical.delivery &&
      this.vertical.delivery.config &&
      this.vertical.delivery.config.report.active
    )
  }

  canShowCSVDownload() {
    return (
      this.vertical.delivery &&
      this.vertical.delivery.config &&
      this.vertical.delivery.config.downloadCSV
    )
  }

  canShowCartoDownload() {
    return (
      this.vertical.delivery &&
      this.vertical.delivery.config &&
      this.vertical.delivery.config.downloadCarto
    )
  }

  toggleDensityMode() {
    const isActive = this.isDensityActive()
    this.densityMode.next(!isActive)
  }

  isDensityActive() {
    return this.densityMode.getValue()
  }
}
